import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Cookie, X } from "lucide-react";
import { Link } from "react-router-dom";

const COOKIE_CONSENT_KEY = "odyvoy_cookie_consent";

// Custom event for opening cookie settings
export const openCookieSettings = () => {
  window.dispatchEvent(new CustomEvent("open-cookie-settings"));
};

export const CookieConsentBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Delay to let users see the content first
      const timer = setTimeout(() => setShowBanner(true), 3000);
      return () => clearTimeout(timer);
    }
  }, []);

  // Listen for custom event to reopen cookie settings
  useEffect(() => {
    const handleOpenSettings = () => {
      setShowDetails(true);
      setShowBanner(true);
    };

    window.addEventListener("open-cookie-settings", handleOpenSettings);
    return () => window.removeEventListener("open-cookie-settings", handleOpenSettings);
  }, []);

  // Let the floating chat button lift out of the way while this banner is up.
  useEffect(() => {
    window.dispatchEvent(new CustomEvent("odyvoy-cookie-banner", { detail: { visible: showBanner } }));
    return () => {
      window.dispatchEvent(new CustomEvent("odyvoy-cookie-banner", { detail: { visible: false } }));
    };
  }, [showBanner]);

  const handleAcceptAll = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify({ type: "all", timestamp: Date.now() }));
    setShowBanner(false);
  };

  const handleAcceptEssential = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify({ type: "essential", timestamp: Date.now() }));
    if (typeof window !== "undefined") {
      (window as any)["ga-disable-GA_MEASUREMENT_ID"] = true;
    }
    setShowBanner(false);
  };

  if (!showBanner) return null;

  // Compact corner pill — doesn't overlap floating chat or block content
  if (!showDetails) {
    return (
      <div className="fixed bottom-3 left-3 right-3 sm:right-auto sm:max-w-sm z-[60] animate-in slide-in-from-bottom duration-300">
        <div className="flex items-center gap-2 rounded-full bg-card/95 backdrop-blur-md border border-border shadow-lg pl-3 pr-1 py-1">
          <Cookie className="h-4 w-4 text-primary shrink-0" />
          <span className="text-xs text-muted-foreground flex-1 truncate">
            We use cookies.{" "}
            <button
              onClick={() => setShowDetails(true)}
              className="text-primary hover:underline"
            >
              Settings
            </button>
          </span>
          <Button onClick={handleAcceptAll} size="sm" className="h-7 text-xs px-3 rounded-full shrink-0">
            Accept
          </Button>
          <Button
            onClick={handleAcceptEssential}
            variant="ghost"
            size="icon"
            className="h-7 w-7 rounded-full shrink-0"
            aria-label="Dismiss"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    );
  }

  // Expanded details view
  return (
    <div className="fixed bottom-0 left-0 right-0 z-[60] animate-in slide-in-from-bottom duration-300">
      <div className="bg-card border-t border-border shadow-lg p-4">
        <div className="container mx-auto max-w-4xl">
          <div className="flex items-start gap-4">
            <div className="hidden sm:flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
              <Cookie className="h-5 w-5 text-primary" />
            </div>

            <div className="flex-1 space-y-3">
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-semibold text-foreground">Cookie Consent</h3>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 shrink-0 -mt-1 -mr-2"
                  onClick={handleAcceptEssential}
                  aria-label="Close"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <p className="text-sm text-muted-foreground">
                We use cookies to enhance your experience. Essential cookies are necessary for the site to function.
                Analytics cookies help us understand how you use our service.{" "}
                <Link to="/privacy" className="text-primary hover:underline">
                  Learn more in our Privacy Policy
                </Link>
              </p>

              <div className="space-y-2 text-sm border-t border-border pt-3 mt-3">
                <div className="flex items-start gap-2">
                  <div className="w-3 h-3 rounded-full bg-primary mt-1 shrink-0" />
                  <div>
                    <p className="font-medium text-foreground">Essential Cookies</p>
                    <p className="text-muted-foreground text-xs">Required for authentication, security, and basic functionality. Cannot be disabled.</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-3 h-3 rounded-full bg-accent mt-1 shrink-0" />
                  <div>
                    <p className="font-medium text-foreground">Analytics Cookies</p>
                    <p className="text-muted-foreground text-xs">Google Analytics with IP anonymization to understand usage patterns and improve the service.</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-3 h-3 rounded-full bg-accent mt-1 shrink-0" />
                  <div>
                    <p className="font-medium text-foreground">Preference Cookies</p>
                    <p className="text-muted-foreground text-xs">Remember your settings like theme preference and temperature units.</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-2 pt-2">
                <Button onClick={handleAcceptAll} size="sm" className="w-full sm:w-auto">
                  Accept All Cookies
                </Button>
                <Button onClick={handleAcceptEssential} variant="outline" size="sm" className="w-full sm:w-auto">
                  Essential Only
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full sm:w-auto text-muted-foreground"
                  onClick={() => setShowDetails(false)}
                >
                  Hide Details
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
